import { courseRuntimeUrl } from './courseRuntimeContext';
import api from '../httpClient';
import type {
  ResponsePayload,
  VoiceFlowSessionBootstrap,
  VoiceFlowProgress,
  VoiceFlowTurnResult,
  VoiceRuntime,
  VoiceProcessingModels,
  VoiceLiveConnectionIdentity,
  VoiceLiveConnectResult,
  VoiceLiveEvaluationResult,
  VoiceLiveNarrationResult,
  VoiceSessionContext,
  VoiceCommandRequest,
} from '../../types';
import { encodeFlowSegment } from './helpers';

const getVoiceSession = (flowName: string, host: number | VoiceSessionContext,
  options?: { includeClientSecret?: boolean; prepareTarget?: boolean; signal?: AbortSignal }) =>
  api.get<VoiceFlowSessionBootstrap>(
    courseRuntimeUrl(`/api/flows/${encodeFlowSegment(flowName)}/voice/session/`),
    { ...(options?.signal ? { signal: options.signal } : {}), params: {
      ...(typeof host === 'number' ? { flow_run_id: host } : { [`${host.kind}_id`]: host.id }),
      ...(options?.includeClientSecret !== undefined ? { include_client_secret: options.includeClientSecret } : {}),
      ...(options?.prepareTarget === false ? { prepare_target: false } : {}),
    } },
  );

export const voiceFlow = {
  transcribeTurn: (flowName: string, sessionId: number, data: {
    audio: Blob; request_id: string; runtime_generation: number; question_id: number | null;
    speech_group_id?: string;
    target_revision?: string;
  }, signal?: AbortSignal) => {
    const form = new FormData();
    form.append('audio', data.audio, 'turn.wav');
    form.append('request_id', data.request_id);
    form.append('runtime_generation', String(data.runtime_generation));
    if (data.question_id !== null) form.append('question_id', String(data.question_id));
    if (data.speech_group_id) form.append('speech_group_id', data.speech_group_id);
    if (data.target_revision) form.append('target_revision', data.target_revision);
    return api.post<{
      request_id: string; runtime_generation: number; question_id: number | null;
      speech_group_id?: string;
      target_revision?: string | null;
      transcript: string; model: 'gpt-transcribe'; cost_estimate: VoiceFlowSessionBootstrap['cost_estimate'];
      outcome?: 'no_speech';
    }>(courseRuntimeUrl(`/api/flows/${encodeFlowSegment(flowName)}/voice/session/${sessionId}/transcription/`), form, {
      headers: { 'Content-Type': 'multipart/form-data' }, signal,
    });
  },
  getSession: getVoiceSession,
  // Each read requests a separate connection credential. Callers consume only
  // its connection facts; this must not replace a newer canonical task state.
  getNarrationConnection: (flowName: string, host: number | VoiceSessionContext, options?: { signal?: AbortSignal }) =>
    getVoiceSession(flowName, host, { ...options, includeClientSecret: true }),
  createSession: (flowName: string, host: number | VoiceSessionContext) =>
    api.post<VoiceFlowSessionBootstrap>(
      courseRuntimeUrl(`/api/flows/${encodeFlowSegment(flowName)}/voice/session/`),
      typeof host === 'number' ? { flow_run_id: host } : { [`${host.kind}_id`]: host.id },
    ),
  command: (flowName: string, sessionId: number, data: VoiceCommandRequest) =>
    api.post<VoiceFlowSessionBootstrap>(
      courseRuntimeUrl(`/api/flows/${encodeFlowSegment(flowName)}/voice/sessions/${sessionId}/command/`), data,
    ),
  persistTurn: (
    flowName: string,
    sessionId: number,
    data: {
      question_id?: number | null;
      runtime_generation?: number;
      speech_group_id?: string;
      user_transcript?: string;
      assistant_transcript?: string;
      provider_session_id?: string | null;
      metadata?: Record<string, unknown> | null;
    },
  ) =>
    api.post<VoiceFlowTurnResult>(
      courseRuntimeUrl(`/api/flows/${encodeFlowSegment(flowName)}/voice/session/${sessionId}/turns/`),
      data,
    ),
  commitResponse: (
    flowName: string,
    sessionId: number,
    data: {
      question_id: number;
      runtime_generation?: number;
      response: ResponsePayload;
    },
  ) =>
    api.post<{
      response: ResponsePayload | null;
      session_status: VoiceFlowSessionBootstrap['status'];
      current_question: VoiceFlowSessionBootstrap['current_question'];
      current_target: VoiceFlowSessionBootstrap['current_target'];
      content_pending?: boolean;
      content_schema: VoiceFlowSessionBootstrap['content_schema'];
      item_states: VoiceFlowSessionBootstrap['item_states'];
      progress: VoiceFlowSessionBootstrap['progress'];
      instruction_block: string;
      latest_extraction?: Record<string, unknown> | null;
      ai_safety?: VoiceFlowSessionBootstrap['ai_safety'];
      assistant_turn_directive: {
        should_create_response: boolean;
        mode: 'normal' | 'supportive_distress' | 'crisis';
        response_purpose?: string;
        disconnect_after_response?: boolean;
      };
      debug?: VoiceFlowSessionBootstrap['debug'];
    }>(
      courseRuntimeUrl(`/api/flows/${encodeFlowSegment(flowName)}/voice/session/${sessionId}/responses/`),
      data,
    ),
  closeSession: (flowName: string, sessionId: number, completed = false, runtimeGeneration?: number) =>
    api.post<{
      id: number;
      status: VoiceFlowSessionBootstrap['status'];
      flow_run_id: number;
      progress?: VoiceFlowProgress;
    }>(
      courseRuntimeUrl(`/api/flows/${encodeFlowSegment(flowName)}/voice/session/${sessionId}/close/`),
      { completed, runtime_generation: runtimeGeneration },
    ),
  clearCrisisPause: (flowName: string, sessionId: number) =>
    api.post<VoiceFlowSessionBootstrap>(
      courseRuntimeUrl(`/api/flows/${encodeFlowSegment(flowName)}/voice/session/${sessionId}/clear-crisis/`),
      {},
    ),
  resetRun: (flowName: string, sessionId: number) =>
    api.post<VoiceFlowSessionBootstrap>(
      courseRuntimeUrl(`/api/flows/${encodeFlowSegment(flowName)}/voice/session/${sessionId}/reset-run/`),
      {},
    ),
  setRealtimeModelOverride: (flowName: string, sessionId: number, realtimeModel: string | null) =>
    api.post<VoiceFlowSessionBootstrap>(
      courseRuntimeUrl(`/api/flows/${encodeFlowSegment(flowName)}/voice/session/${sessionId}/realtime-model/`),
      { realtime_model: realtimeModel },
    ),
  setTranscriptionModelOverride: (flowName: string, sessionId: number, transcriptionModel: string | null) =>
    api.post<VoiceFlowSessionBootstrap>(
      courseRuntimeUrl(`/api/flows/${encodeFlowSegment(flowName)}/voice/session/${sessionId}/transcription-model/`),
      { transcription_model: transcriptionModel },
    ),
  setRuntimeOverride: (flowName: string, sessionId: number, runtime: VoiceRuntime | null) =>
    api.post<VoiceFlowSessionBootstrap>(
      courseRuntimeUrl(`/api/flows/${encodeFlowSegment(flowName)}/voice/session/${sessionId}/runtime/`),
      { runtime },
    ),
  setProcessingModelsOverride: (
    flowName: string,
    sessionId: number,
    models: Partial<VoiceProcessingModels> | null,
    runtimeGeneration: number,
  ) => api.post<VoiceFlowSessionBootstrap>(
    courseRuntimeUrl(`/api/flows/${encodeFlowSegment(flowName)}/voice/session/${sessionId}/processing-models/`),
    { models, runtime_generation: runtimeGeneration },
  ),
  getProcessingModels: (flowName: string, sessionId: number) =>
    api.get<VoiceFlowSessionBootstrap>(
      courseRuntimeUrl(`/api/flows/${encodeFlowSegment(flowName)}/voice/session/${sessionId}/processing-models/`),
    ),
  connectLive: (flowName: string, sessionId: number, data: {
    sdp: string; request_id: string; runtime_generation: number;
  }) => api.post<VoiceLiveConnectResult>(
    courseRuntimeUrl(`/api/flows/${encodeFlowSegment(flowName)}/voice/session/${sessionId}/live/connect/`), data,
  ),
  evaluateLive: (flowName: string, sessionId: number, data: VoiceLiveConnectionIdentity & {
    capture_id: string; revision: number; user_transcript: string; assistant_transcript?: string; assistant_capture_id?: string;
    target_revision?: string;
    captured_at?: string; assistant_captured_at?: string;
  }) => api.post<VoiceLiveEvaluationResult>(
    courseRuntimeUrl(`/api/flows/${encodeFlowSegment(flowName)}/voice/session/${sessionId}/live/evaluate/`), data,
  ),
  commitLive: (flowName: string, sessionId: number, data: VoiceLiveConnectionIdentity & {
    evaluation_id: string;
  }) => api.post<VoiceFlowTurnResult>(
    courseRuntimeUrl(`/api/flows/${encodeFlowSegment(flowName)}/voice/session/${sessionId}/live/commit/`), data,
  ),
  persistLiveAssistant: (flowName: string, sessionId: number, data: VoiceLiveConnectionIdentity & {
    capture_id: string; assistant_transcript: string; narration_token?: string | null; captured_at?: string;
  }) => api.post<Partial<VoiceFlowSessionBootstrap>>(
    courseRuntimeUrl(`/api/flows/${encodeFlowSegment(flowName)}/voice/session/${sessionId}/live/assistant/`), data,
  ),
  acknowledgeLiveNarration: (flowName: string, sessionId: number, data: VoiceLiveConnectionIdentity & {
    narration_token: string; assistant_transcript: string; played: boolean; assistant_capture_ids?: string[];
    confirmation_mode?: 'automatic' | 'user';
  }) => api.post<VoiceLiveNarrationResult>(
    courseRuntimeUrl(`/api/flows/${encodeFlowSegment(flowName)}/voice/session/${sessionId}/live/narration/`), data,
  ),
  recordLiveUsage: (flowName: string, sessionId: number, data: VoiceLiveConnectionIdentity & {
    usage: { seconds: number }; final_usage_confirmed: boolean;
  }) => api.post<Partial<VoiceFlowSessionBootstrap>>(
    courseRuntimeUrl(`/api/flows/${encodeFlowSegment(flowName)}/voice/session/${sessionId}/live/usage/`), data,
  ),
  closeLive: (flowName: string, sessionId: number, data: VoiceLiveConnectionIdentity & {
    usage?: { seconds: number }; final_usage_confirmed: boolean;
  }) => api.post<Partial<VoiceFlowSessionBootstrap>>(
    courseRuntimeUrl(`/api/flows/${encodeFlowSegment(flowName)}/voice/session/${sessionId}/live/close/`), data,
  ),
  recordUsageEvent: (
    flowName: string,
    sessionId: number,
    data: {
      event_type: 'response.done' | 'conversation.item.input_audio_transcription.completed';
      runtime_generation?: number;
      event_id: string;
      model: string;
      usage?: Record<string, unknown> | null;
      duration_seconds?: number | null;
    },
  ) =>
    api.post<{ cost_estimate: VoiceFlowSessionBootstrap['cost_estimate'] }>(
      courseRuntimeUrl(`/api/flows/${encodeFlowSegment(flowName)}/voice/session/${sessionId}/usage-events/`),
      data,
    ),
  getSafetyNarration: (flowName: string, sessionId: number) =>
    api.get<{
      audio_url: string;
      mime_type: string;
      cache_key: string;
      notice_type: 'crisis';
    }>(
      courseRuntimeUrl(`/api/flows/${encodeFlowSegment(flowName)}/voice/session/${sessionId}/safety-narration/`),
    ).then((response) => response.data),
};
